import {
  PROTOCOL_VERSION,
  WS_CLOSE,
  type ServerEvent,
  type ServerFrame,
  type Topic,
} from '@cyphex/types';
import { CLIENT_VERSION, gatewayUrl } from '@/lib/api/env';
import { getLiveAccessToken, onAccessTokenChange } from '@/lib/api/token';
import { refreshAccessToken } from '@/lib/api/refresh';
import { newUuid } from '@/lib/ids';
import { backoffMs } from './backoff';
import { createIdleClock } from './idleClock';
import { Sequencer } from './sequencer';
import type {
  ConnectionState,
  LiveRealtimeClient,
  OutgoingFrame,
  RealtimeDelivery,
} from './types';

const DEFAULT_HEARTBEAT_MS = 25_000;
const HEARTBEAT_MARGIN_MS = 5_000;
/**
 * THE FLOOR UNDER `POST /auth/refresh`, and it is not a nicety.
 *
 * `connect()` refreshes when it has no usable token, and a successful refresh
 * fires `onAccessTokenChange`, which calls `connect()` again. That is a closed
 * loop with no backoff in it, and it spins whenever the token it gets back is
 * ALSO unusable — a client clock far enough ahead of the server's that every
 * token arrives inside `EXPIRY_SKEW_MS`, or a deployment that sets a short
 * `ACCESS_TOKEN_TTL_S` (the API validates it as `positive()` and nothing more,
 * so `20` is accepted). Measured at ~17 rotations per second against an
 * endpoint that rate-limits at 120 and rotates a SINGLE-USE refresh family on
 * every call: the reuse detector reads the second one as theft and revokes the
 * session. An outage caused by the code that repairs outages.
 *
 * So a refresh-driven attempt waits its turn on the ordinary backoff instead.
 * Nothing else is throttled: a live token connects immediately, and a login
 * refreshes through `AuthProvider`, not through here.
 */
const REFRESH_MIN_INTERVAL_MS = 5_000;
const RESYNC_LIMIT = 500;
const ACK_INTERVAL_MS = 1_000;

interface Listeners {
  events: Set<(delivery: RealtimeDelivery) => void>;
  states: Set<(state: ConnectionState) => void>;
  resets: Set<(topic: Topic | null) => void>;
  pressure: Set<(level: 'warn' | 'shedding' | 'critical') => void>;
}

/**
 * The live gateway transport (docs/REALTIME-PROTOCOL.md).
 *
 * One socket per tab, never one per conversation. Topics are subscribed in
 * batches; `resume` cursors mean a reconnect replays only what was missed
 * instead of refetching every open timeline; the heartbeat — not a polling
 * loop — is what keeps presence alive; and every gap repairs itself through
 * `resync` before a single out-of-order frame reaches the query cache.
 */
export function createRealtimeClient(): LiveRealtimeClient {
  const listeners: Listeners = { events: new Set(), states: new Set(), resets: new Set(), pressure: new Set() };
  const seq = new Sequencer();
  const topics = new Set<Topic>();

  let socket: WebSocket | null = null;
  let state: ConnectionState = 'offline';
  let sessionId: string | null = null;
  let attempt = 0;
  let heartbeatMs = DEFAULT_HEARTBEAT_MS;
  let heartbeat: ReturnType<typeof setInterval> | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let closedByUs = false;
  /**
   * THE CLOCK THE SERVER SEES. Every rule it has is in ./idleClock.ts, which is
   * dependency-free so that it can actually be executed by a test - this file
   * needs a WebSocket, a token, a document and a network to instantiate at all,
   * and a rule nothing can reach is a rule nobody checks.
   */
  const idle = createIdleClock();
  let lastFrameId: string | null = null;
  let lastAckAt = 0;
  /**
   * A token the GATEWAY has refused as expired, whatever its own `exp` said.
   *
   * `getLiveAccessToken` reads `exp` and is right almost always; two clocks
   * that disagree are the case where it is not. Without this the loop the
   * expiry check exists to break comes straight back: refused, reconnect,
   * present the same string, refused. One rejection retires a token for good.
   */
  let rejectedToken: string | null = null;
  /** The token this socket handshook with, so a 4403 can name the dead one. */
  let presentedToken: string | null = null;
  /** When `connect()` last asked for a refresh. See REFRESH_MIN_INTERVAL_MS. */
  let lastRefreshAt = 0;

  const setState = (next: ConnectionState): void => {
    if (state === next) return;
    state = next;
    listeners.states.forEach((handler) => handler(next));
  };

  const send = (frame: OutgoingFrame): void => {
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ ...frame, v: PROTOCOL_VERSION, id: newUuid() }));
  };

  const reset = (topic: Topic | null): void => {
    seq.reset(topic ?? undefined);
    listeners.resets.forEach((handler) => handler(topic));
  };

  const deliver = (frame: ServerEvent): void => {
    seq.commit(frame.topic, frame.seq);
    listeners.events.forEach((handler) =>
      handler({ topic: frame.topic, seq: frame.seq, event: frame.event }),
    );
  };

  const stopHeartbeat = (): void => {
    if (heartbeat !== null) clearInterval(heartbeat);
    heartbeat = null;
  };

  /*
   * THE PING IS A CLAIM ABOUT A HUMAN, AND IT IS UNCONDITIONAL.
   *
   * Every document that opens a socket runs this timer - the workspace, and a
   * desktop CALL WINDOW, which mounts `RealtimeRuntime` without
   * `PresenceRuntime` and therefore measures nothing at all. `dispatch.ts:209`
   * reads whatever number arrives as `applyActivity(conn, idle <
   * conn.autoAwayMs)`, so there is no distinction available HERE between a
   * measurement and a guess; the distinction is made once, inside the clock,
   * and there is no other gate. Two rounds fixed `framesFor` and left this
   * line, and a locked workstation read AVAILABLE to every colleague for five
   * minutes each time. See ./idleClock.ts.
   */
  const startHeartbeat = (authenticated: boolean): void => {
    stopHeartbeat();
    const period = Math.max(5_000, heartbeatMs - HEARTBEAT_MARGIN_MS);
    /*
     * THE FIRST PING RETRACTS THE ARRIVAL - BUT ONLY ONCE THE SERVER WILL
     * ACCEPT ONE.
     *
     * ARRIVING IS ITSELF A VOTE, and it was the last way a document that has
     * measured nothing could still claim a human. The gateway counts a socket
     * ACTIVE from the moment it arrives (`redis/presence.ts ensureCounted` ->
     * `onConnect`: `hincrby(k, "a", 1)`, `hset(k, "st", "available")`) and
     * resolves the PERSON across all of their sockets with
     * `activeCount > 0 ? "available" : "away"`. A connection that has said
     * nothing yet is therefore not neutral: it is already claiming somebody is
     * at the keyboard, and `idleMs()` is the ONLY thing that can retract it.
     *
     * `setInterval` fires FIRST at `period`, so that retraction was 20 s late
     * (a 25 s heartbeat less the 5 s margin) on every connection. For the
     * workspace the gap is invisible, because it has a human and the vote is
     * true. For a desktop CALL WINDOW - which mounts `RealtimeRuntime` without
     * `PresenceRuntime` and so measures nothing at all - the vote is a
     * fabrication, and it stood for twenty seconds AFTER EVERY RECONNECT, FOR
     * EVER: a token refresh, a network blip or a gateway deploy re-greened a
     * locked workstation each time. Executed: lock the screen, drop the call
     * window's socket, and every colleague saw AVAILABLE again for 20 s.
     *
     * `authenticated` IS NOT A TIDINESS ARGUMENT, IT IS THE WHOLE SAFETY OF
     * THIS LINE. `startHeartbeat` is called TWICE - once from `onopen`, right
     * after the `hello` goes out, and again from `hello.ok` - and the gateway
     * closes any socket that sends a non-`hello` frame before it is
     * authenticated (`core/dispatch.ts`: `if (!conn.authed) acceptHandshake`,
     * which does `conn.closeWith(WS_CLOSE.UNAUTHENTICATED,
     * 'handshake_required')` for any other `t`). `handleHello` is awaited off a
     * `void` promise, so a ping sent from `onopen` can genuinely arrive first.
     * An unconditional immediate ping here kills every connection in a
     * reconnect loop and takes presence down with it - so the pre-auth caller
     * passes `false`, and only the `hello.ok` caller may retract.
     *
     * It costs one small frame per connection. `handlePing` is a no-op for the
     * honest case: `applyActivity` returns early when the flag already agrees
     * (`core/handlers.ts`: `if (wasActive === active) return;`), so a present
     * user pays a `queueRefresh` and nothing else.
     *
     * The race this opens with the arrival it retracts is the GATEWAY'S to
     * close, and it does. `core/handshake.ts` sends `hello.ok` BEFORE it
     * awaits `ensureCounted`, so this ping routinely reaches `handlePing`
     * while the arrival is still in flight. This block used to argue that was
     * harmless because addition commutes - true of the counter, false of `st`
     * and of the publication, which the arrival wrote LAST and hard-coded to
     * Available. Now an uncounted socket moves only its flag, and the arrival
     * counts it from that flag and reconciles anything that moved meanwhile
     * (apps/gateway/src/redis/presence.ts `ensureCounted`).
     */
    if (authenticated) send({ t: 'ping', idleMs: idle.idleMs() });
    heartbeat = setInterval(() => send({ t: 'ping', idleMs: idle.idleMs() }), period);
  };

  /** Lets the server measure real drain rate instead of TCP buffering (§10). */
  const maybeAck = (): void => {
    const now = Date.now();
    if (lastFrameId === null || now - lastAckAt < ACK_INTERVAL_MS) return;
    lastAckAt = now;
    send({ t: 'ack', upTo: lastFrameId });
  };

  const handleEvent = (frame: ServerEvent): void => {
    switch (seq.admit(frame)) {
      case 'drop':
        return;
      case 'gap':
        seq.hold(frame);
        send({ t: 'resync', topic: frame.topic, fromSeq: seq.highWater(frame.topic), limit: RESYNC_LIMIT });
        return;
      default:
        deliver(frame);
    }
  };

  const handle = (frame: ServerFrame): void => {
    if (seq.isDuplicateFrame(frame.id)) return;
    lastFrameId = frame.id;

    switch (frame.t) {
      case 'hello.ok': {
        sessionId = frame.sessionId;
        attempt = 0;
        heartbeatMs = frame.heartbeatIntervalMs;
        startHeartbeat(true);
        setState('online');
        /* Resume refused: local cursors are meaningless, cold-load everything. */
        if (!frame.resumed) reset(null);
        frame.subscribed.forEach((topic) => topics.add(topic));
        const missing = [...topics].filter((topic) => !frame.subscribed.includes(topic));
        if (missing.length > 0) send({ t: 'sub', topics: missing, cursors: seq.cursorsFor(missing) });
        break;
      }
      case 'sub.ok':
        Object.entries(frame.topics).forEach(([topic, high]) => seq.seed(topic, high));
        frame.denied?.forEach((entry) => topics.delete(entry.topic));
        break;
      case 'event':
        handleEvent(frame);
        maybeAck();
        break;
      case 'catchup.begin':
        seq.beginCatchup(frame.topic);
        break;
      case 'catchup.end':
        seq.endCatchup(frame.topic);
        seq.release(frame.topic).forEach(deliver);
        if (frame.truncated) {
          send({ t: 'resync', topic: frame.topic, fromSeq: frame.toSeq, limit: RESYNC_LIMIT });
        }
        break;
      case 'resync.too_far':
        reset(frame.topic);
        break;
      case 'backpressure':
        listeners.pressure.forEach((handler) => handler(frame.level));
        break;
      case 'error':
        if (frame.code === 'unauthenticated' || frame.code === 'token_expired') sessionId = null;
        break;
      case 'reconnect':
        if (!frame.resumable) sessionId = null;
        /*
         * THE WARNING WAS BEING WASTED. The gateway sends this 60 seconds
         * before it will close the socket at `exp`
         * (apps/gateway/src/core/heartbeat.ts), which is a whole minute of
         * notice — and this branch closed the socket and reconnected with the
         * very token that was about to die. Go and get a live one first; the
         * reconnect is driven by `onAccessTokenChange` the moment it lands, and
         * by the ordinary backoff if the refresh fails.
         */
        if (frame.reason === 'token_expired') rejectedToken = presentedToken;
        socket?.close(WS_CLOSE.NORMAL);
        break;
      default:
        break;
    }
  };

  /**
   * THE TOKEN THIS CLIENT MAY PRESENT, or null.
   *
   * Two independent reasons a held token is no good, and both have already cost
   * a permanent stall: it is dead by its own `exp` (the 68-minute call), or the
   * gateway has refused this exact string whatever `exp` said (two clocks that
   * disagree). Asked in ONE place because the refresh branch below has to ask
   * the identical question afterwards — "can I connect now?" — and answering it
   * a second, slightly different way is how the last hole was left open.
   */
  const usableToken = (): string | null => {
    const held = getLiveAccessToken();
    return held !== null && held === rejectedToken ? null : held;
  };

  const scheduleReconnect = (afterMs?: number): void => {
    if (closedByUs || retry !== null) return;
    const delay = afterMs ?? backoffMs(attempt);
    attempt += 1;
    retry = setTimeout(() => {
      retry = null;
      client.connect();
    }, delay);
  };

  const client: LiveRealtimeClient = {
    state: () => state,

    connect() {
      if (socket !== null && socket.readyState <= WebSocket.OPEN) return;
      /*
       * AN EXPIRED TOKEN IS NO TOKEN — see `getLiveAccessToken`, and the
       * 68-minute call it is named after. Presenting one gets the handshake
       * refused with 4403, which schedules another reconnect, which presented
       * it again; the loop below is capped at 30s and never ended, and every
       * durable event in the product — a call ending, a message, presence —
       * was delivered to a topic with nobody on it for as long as it ran.
       */
      const token = usableToken();
      if (token === null) {
        /*
         * NO TOKEN IS NOT A REASON TO GIVE UP — IT IS THE COMMONEST REASON TO
         * BE HERE.
         *
         * This used to `return` and wait to be woken by `onAccessTokenChange`.
         * Nothing wakes it. The access token is memory-only and short-lived,
         * and the ONLY thing that refreshes it is an outgoing HTTP request
         * (lib/api/client.ts). So on a tab left idle:
         *
         *   token expires -> socket closes (gateway idle timeout or network)
         *   -> onclose schedules a reconnect -> the reconnect finds no token
         *   -> returns, scheduling NOTHING -> no request is ever made, so no
         *      refresh happens, so onAccessTokenChange never fires
         *
         * and the client sits at "You are offline" until the page is reloaded,
         * with presence still reading Available to everybody else. That is the
         * reported bug, and the reload is what people had to do about it.
         *
         * So: go and get one. `refreshAccessToken` is single-flight, so several
         * of these collapse into one call. If the refresh fails the session may
         * be genuinely gone — keep retrying on the ordinary backoff (capped at
         * 30s) rather than dying silently, because the failure may equally be a
         * network still coming back.
         */
        /* One rotation per REFRESH_MIN_INTERVAL_MS at the outside. A second
           attempt inside the window is not dropped — it waits on the ordinary
           retry, which is the same road every other failure takes. */
        const since = Date.now() - lastRefreshAt;
        if (since < REFRESH_MIN_INTERVAL_MS) {
          scheduleReconnect(REFRESH_MIN_INTERVAL_MS - since);
          return;
        }
        lastRefreshAt = Date.now();
        void refreshAccessToken()
          .then(() => {
            /*
             * ASK AGAIN, DO NOT COMPARE STRINGS.
             *
             * This tested `fresh === null || fresh === rejectedToken`, which
             * covers exactly one way of coming back with nothing usable: a
             * token the gateway has already refused with 4403. `rejectedToken`
             * is set by nothing else, so after a 4408 (a backgrounded tab
             * reaped by the heartbeat), a 4503 (a gateway restart) or a 1006,
             * it is still null — and a refresh that hands back the SAME dead
             * string, which is what the desktop shell broker does when the
             * workspace window is gone or its rotation request times out
             * (main/authBroker.ts), satisfied neither test. Nothing was
             * scheduled; `setAccessToken` early-returns on an unchanged value
             * so no listener fired either. Offline for the rest of the session,
             * one socket ever opened — the same permanent stall this whole
             * change is named after, one door along.
             *
             * The question is not "what did the refresh return", it is "can I
             * connect now". `usableToken` answers it, for every close code and
             * every broker, including the ones not invented yet.
             */
            if (usableToken() === null) scheduleReconnect();
            /* Otherwise onAccessTokenChange reconnects the moment it lands. */
          })
          .catch(() => scheduleReconnect());
        return;
      }

      closedByUs = false;
      presentedToken = token;
      setState('connecting');
      socket = new WebSocket(gatewayUrl());

      socket.onopen = () => {
        send({
          t: 'hello',
          token,
          clientVersion: CLIENT_VERSION,
          ...(sessionId === null ? {} : { resume: { sessionId, cursors: seq.cursors() } }),
          subscribe: [...topics],
        });
        startHeartbeat(false);
      };

      socket.onmessage = (message: MessageEvent<string>) => {
        try {
          handle(JSON.parse(message.data) as ServerFrame);
        } catch {
          /* A malformed frame must never take the socket down. */
        }
      };

      socket.onclose = (event: CloseEvent) => {
        stopHeartbeat();
        socket = null;
        setState('offline');
        /* 4401/4400 are not resumable and not retryable: a reconnect loop here
           would hammer the gateway forever. Auth (or a reload) must intervene. */
        if (event.code === WS_CLOSE.UNAUTHENTICATED || event.code === WS_CLOSE.BAD_VERSION) {
          sessionId = null;
          return;
        }
        /*
         * 4403 IS resumable — but ONLY with a different token, and this is
         * where that used to be assumed rather than done. The comment here
         * said "refresh happens over REST and the token listener reconnects",
         * which is true of a tab that makes requests and false of the one tab
         * that matters: a tab sitting in a call issues no HTTP at all, so no
         * refresh was ever triggered and the reconnect below re-presented the
         * dead token for ever. Retiring it sends `connect()` down its
         * refresh-first branch instead.
         */
        if (event.code === WS_CLOSE.TOKEN_EXPIRED) rejectedToken = presentedToken;
        scheduleReconnect();
      };
    },

    close() {
      closedByUs = true;
      if (retry !== null) clearTimeout(retry);
      retry = null;
      stopHeartbeat();
      socket?.close(WS_CLOSE.NORMAL);
    },

    subscribe(next) {
      const added = next.filter((topic) => !topics.has(topic));
      added.forEach((topic) => topics.add(topic));
      if (added.length > 0) send({ t: 'sub', topics: added, cursors: seq.cursorsFor(added) });
    },

    unsubscribe(next) {
      const removed = next.filter((topic) => topics.has(topic));
      removed.forEach((topic) => topics.delete(topic));
      if (removed.length > 0) send({ t: 'unsub', topics: removed });
    },

    typing(conversationId, typingState, threadRootId) {
      /* A keystroke - but evidence only where the lock can reach it, which a
         call window's chat is not. See lib/realtime/idleClock.ts. */
      idle.noteInput();
      send({
        t: 'typing',
        conversationId,
        state: typingState,
        ...(threadRootId === undefined ? {} : { threadRootId }),
      });
    },

    /*
     * A FRAME, AND ONLY A FRAME.
     *
     * `active` used to call `idle.note()` as well, and that one line was the
     * hole this whole clock exists to close. The callers that send it are
     * `RealtimeRuntime.report()` on mount, on a reconnect and on a window
     * becoming visible — three things that BELIEVE a human is here rather than
     * having seen one. Writing that belief into the measurement slot made it
     * unfalsifiable, because the clock only moves forward: lock your screen and
     * reconnect, or reload beside an empty desk, and the OS could no longer
     * contradict it. Every colleague read Available for the next five minutes.
     *
     * So the frame says what the caller concluded and the clock keeps only what
     * was measured. The two are reconciled where the conclusion is drawn
     * (features/presence/systemActivity.ts), from this clock and the OS.
     */
    activity(activity) {
      send({ t: 'presence', activity });
    },

    noteActivity(at) {
      idle.note(at);
    },

    setAbsent(absent) {
      idle.setAbsent(absent);
    },

    assumeOpenedByHuman() {
      idle.assumeOpenedByHuman();
    },

    resetIdleEvidence() {
      idle.forget();
    },

    send,

    onEvent(handler) {
      listeners.events.add(handler);
      return () => {
        listeners.events.delete(handler);
      };
    },

    onState(handler) {
      listeners.states.add(handler);
      return () => {
        listeners.states.delete(handler);
      };
    },

    onTopicReset(handler) {
      listeners.resets.add(handler);
      return () => {
        listeners.resets.delete(handler);
      };
    },

    onBackpressure(handler) {
      listeners.pressure.add(handler);
      return () => {
        listeners.pressure.delete(handler);
      };
    },
  };

  /*
   * COME BACK THE MOMENT THE USER DOES.
   *
   * Reconnecting was driven ENTIRELY by a `setTimeout` from `onclose`, and a
   * timer is the one thing a background tab cannot rely on. Chrome freezes
   * backgrounded tabs outright under the Page Lifecycle API — timers do not
   * fire at all while frozen — and throttles them heavily before that. So:
   *
   *   tab goes to the background -> gateway closes the socket on its idle
   *   timeout -> onclose schedules a retry -> the tab freezes and the retry
   *   never runs -> the user comes back to "You are offline" and the only way
   *   out is a reload.
   *
   * Which is precisely the report. The fix is to stop waiting for a timer and
   * listen for the events that mean "a human is looking at this again":
   *
   *   visibilitychange  the tab was foregrounded
   *   pageshow          restored from the bfcache (onclose may never have run)
   *   focus             the window was refocused without a visibility change
   *   online            the network came back
   *
   * `attempt` is reset first, deliberately. The backoff exists to protect the
   * gateway from a reconnect storm after a restart, and it is capped at 30s —
   * but coming back to a tab is NOT a failure, and making somebody stare at a
   * stale "offline" for up to half a minute is exactly the symptom. A resume
   * reconnects at once.
   */
  const wake = (): void => {
    if (closedByUs) return;
    if (socket !== null && socket.readyState <= WebSocket.OPEN) return;
    if (retry !== null) {
      clearTimeout(retry);
      retry = null;
    }
    attempt = 0;
    client.connect();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);
    window.addEventListener('focus', wake);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') wake();
      });
    }
  }

  /* A fresh token after a refresh or a login must reconnect immediately. */
  onAccessTokenChange((token) => {
    if (token === null) {
      client.close();
      return;
    }
    closedByUs = false;
    if (socket === null) client.connect();
  });

  return client;
}
